import request from 'app/utils/request';
import { History } from 'app/services/history';
import { dmmCallbackUrl, dmmCookieCheck } from 'app/utils/api';
import { hasDMMCookie } from 'app/services/cookies/dmm';
import { LoginIntent } from 'app/services/oauth/constants';
import * as t from 'io-ts';
import { decodeOrThrow } from 'app/utils/ioTs';

const DMMCookieCheckResponse = t.interface({
  data: t.boolean,
});

const DMMCallbackUrlResponse = t.interface({
  data: t.string,
});

export enum DMMLoginState {
  HAS_REGISTERED_DMM_LOGIN,
  HAS_UNREGISTERED_DMM_LOGIN,
  NO_DMM_LOGIN,
}

export interface IDMMAuthService {
  auth: (intent: LoginIntent, state: string) => Promise<string>;
  isDMMLoginStateKnown: () => boolean;
  getDMMLoginStateSync: () => DMMLoginState | undefined;
  fetchDMMLoginState: () => Promise<DMMLoginState>;
}

// These are part of the upstream API, and must not depend on our internal constants.
const DMMActionParams = {
  [LoginIntent.Register]: { actionName: 'register', pathParam: 'back_url' },
  [LoginIntent.Login]:    { actionName: 'login', pathParam: 'path' },
};

export class DMMAuthService implements IDMMAuthService {
  public constructor(
    public readonly history: History,
  ) {
    this.history = history;
  }

  public isDMMLoginStateKnown(): boolean {
    return this.getDMMLoginStateSync() !== undefined;
  }

  public getDMMLoginStateSync(): DMMLoginState | undefined {
    // If we have no DMM cookie, then we can determine the login state synchronously without making
    // an API request.
    //
    // Note that the dmm_check API actually uses the INT_SESID cookie instead of the
    // DMM_AUTH_COOKIE which is being checked for here. These cookies are controlled by DMM,
    // however, they are linked together closely enough that checking for the DMM_AUTH_COOKIE
    // is implicitly a check for the INT_SESID.
    return (
      !hasDMMCookie() ? DMMLoginState.NO_DMM_LOGIN
      /* else */      : undefined
    );
  }

  public async fetchDMMLoginState(): Promise<DMMLoginState> {
    const state = this.getDMMLoginStateSync();

    if (state !== undefined) {
      return state;
    } else {
      const dmmCheckResult = await this.isLoggedInExistingUser();

      return (
        dmmCheckResult === true ? DMMLoginState.HAS_REGISTERED_DMM_LOGIN
        /* else */              : DMMLoginState.HAS_UNREGISTERED_DMM_LOGIN
      );
    }
  }

  public async auth(intent: LoginIntent, state: string): Promise<string> {
    const returnURL = await this.encodeReturnURL(state);

    const { actionName, pathParam } = DMMActionParams[intent];

    this.history.hardPush(
      `https://www.dmm.com/my/-/${actionName}/=/${pathParam}=${returnURL}`);

    // Block forever until history takes effect
    return new Promise<string>(() => {});
  }

  private async encodeReturnURL(state: string): Promise<string> {
    const response = await request(dmmCallbackUrl(state));
    return decodeOrThrow(DMMCallbackUrlResponse, response).data;
  }

  // Returns true if the use is logged in to DMM, and is also a
  // registered user with us.
  private async isLoggedInExistingUser(): Promise<boolean> {
    try {
      const result = await request(dmmCookieCheck());

      return decodeOrThrow(DMMCookieCheckResponse, result).data;
    } catch (e) {
      return false;
    }
  }
}
